import { useEffect, useState, useRef } from 'react';

type Options = {
  root?: Element | null;
  rootMargin?: string;
  threshold?: number | number[];
};

export const useIsVisible = <T extends Element = Element>(
  options: Options = { root: null, rootMargin: '0px', threshold: 0 },
): [boolean, React.MutableRefObject<any>] => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<T>(null);
  const { root, rootMargin, threshold } = options;

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      setIsVisible(false);
      return;
    }

    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting);
        });
      },
      { root, rootMargin, threshold },
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [ref.current, root, rootMargin, threshold]);

  return [isVisible, ref];
};
